import styled from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import { VerticalBreak } from '../commonStyles'
import {
  useCheckout,
  useConsumerCart,
  useShop,
  useFulfillment,
  useShopPage,
  useCartMatch
} from 'shop/hooks'
import { FulfillmentType } from 'shop/types'
import FulfillmentToggle from './FulfillmentToggle'
import FulfillmentSummary from './FulfillmentSummary'
import {
  CartErrorMessage,
  CartMutationResponse,
  OrderItemWithLimit
} from 'shop/types/cart'
import { getCurrentProductErrors, getInvalidItems } from 'shop/utils'
import { trackFulfillmentTypeChange } from 'shop/utils/common'

interface Props {
  isStickyMobile?: boolean
}

/** Encapsulates fulfillment change UI components on the shop page */
const Fulfillment = ({ isStickyMobile = false }: Props) => {
  const { currentFulfillmentType } = useFulfillment()
  const {
    handleFulfillmentTypeChange,
    fulfillmentToggleDataLoading,
    createDefaultCart,
    updateExceededLimitItems,
    setIsFulfillmentToggleTriggered
  } = useCheckout()
  const { setShouldReloadProducts, setProductErrorOnContinue } = useShopPage()
  const { cartLoading, cart, removeOrderItemsConsumerCart } = useConsumerCart()
  const { currentPartnerStore } = useShop()
  const { isCartMatch } = useCartMatch()

  /** Handles successful fulfillment change via fulfillment toggle */
  const handleFulfillmentChangeSuccess = (
    fulfillmentType: FulfillmentType,
    orderType: string
  ) => {
    // Track fulfillment type change via toggle if successful
    // Pass orderType explicitly from cart response to avoid stale orderType being passed
    trackFulfillmentTypeChange(fulfillmentType, 'shop', orderType)
    // Trigger product reload directly
    setShouldReloadProducts(true)
    // Reset fulfillment toggle triggered flag
    setIsFulfillmentToggleTriggered(false)
  }

  /** Executes the fulfillment toggle onClick */
  const executeFulfillmentChange = (
    projectedFulfillmentType: FulfillmentType
  ) => {
    if (!currentPartnerStore) return

    // If no cart exists, create a default cart with the selected fulfillment type
    if (!cart) {
      createDefaultCart(currentPartnerStore, projectedFulfillmentType).then(
        (cart) => {
          if (cart) {
            const orderType = cart.fulfillment.orderType
            handleFulfillmentChangeSuccess(projectedFulfillmentType, orderType)
          }
        }
      )
      return
    }

    /** Recursive function to handle errors or success from fulfillment toogle attempts */
    const handleErrorsOrSuccess = (
      updatedCartRes: CartMutationResponse | null
    ) => {
      const { cart: updatedCart, errors } = updatedCartRes || {}

      // Product errors handling
      if (errors?.length) {
        const currentProductErrors = getCurrentProductErrors(errors)

        if (currentProductErrors?.length) {
          const invalidItems = getInvalidItems(
            currentProductErrors[0],
            cart.orderItems
          )

          const hasPriceChangedError = currentProductErrors?.some(
            (err) => err.message === CartErrorMessage.PRODUCT_PRICES_CHANGED
          )
          const hasInvalidItemsError = currentProductErrors?.some(
            (err) =>
              err.message === CartErrorMessage.INVALID_MODIFIERS ||
              err.message === CartErrorMessage.PRODUCTS_UNAVAILABLE ||
              err.message === CartErrorMessage.PRODUCTS_OUT_OF_STOCK
          )
          const hasLimitedReachedError = currentProductErrors?.some(
            (err) => err.message === CartErrorMessage.PRODUCT_LIMIT_REACHED
          )

          if (hasLimitedReachedError) {
            setProductErrorOnContinue(() => () => {
              return updateExceededLimitItems(
                invalidItems as OrderItemWithLimit[]
              )
                .then(() => {
                  return handleFulfillmentTypeChange(projectedFulfillmentType)
                })
                .then(handleErrorsOrSuccess)
            })
            return
          }

          if (hasPriceChangedError) {
            setProductErrorOnContinue(() => () => {
              return handleFulfillmentTypeChange(
                projectedFulfillmentType,
                true
              ).then(handleErrorsOrSuccess)
            })
            return
          }

          if (hasInvalidItemsError) {
            const items = getInvalidItems(
              currentProductErrors[0],
              cart.orderItems
            )
            setProductErrorOnContinue(() => () => {
              const itemIds = items.map((item) => item.id)
              return removeOrderItemsConsumerCart({ ids: itemIds }).then(() => {
                return handleFulfillmentTypeChange(
                  projectedFulfillmentType
                ).then(handleErrorsOrSuccess)
              })
            })
            return
          }
        }
      }

      // Success handling
      if (updatedCart) {
        const orderType = updatedCart.fulfillment?.orderType
        handleFulfillmentChangeSuccess(projectedFulfillmentType, orderType)
      }
    }

    handleFulfillmentTypeChange(projectedFulfillmentType).then(
      handleErrorsOrSuccess
    )
  }

  /** Handles fulfillment toggle click */
  const handleFulfillmentToggleClick = (
    projectedFulfillmentType: FulfillmentType
  ) => {
    if (
      fulfillmentToggleDataLoading ||
      cartLoading ||
      !currentPartnerStore ||
      !currentFulfillmentType
    )
      return
    if (projectedFulfillmentType === currentFulfillmentType) return
    executeFulfillmentChange(projectedFulfillmentType)
  }

  return (
    <>
      <Container
        data-testid='fulfillment-container'
        isStickyMobile={isStickyMobile}
      >
        {currentFulfillmentType && (
          <>
            <FulfillmentToggleWrapper>
              <FulfillmentToggle
                isCartMatch={isCartMatch}
                currentFulfillmentType={currentFulfillmentType}
                handleFulfillmentToggleClick={handleFulfillmentToggleClick}
              />
            </FulfillmentToggleWrapper>
            <VerticalBreak margin={'4px 0 4px'} />
          </>
        )}
        <SummaryContent>
          <FulfillmentSummary />
        </SummaryContent>
      </Container>
    </>
  )
}

const Container = styled.div<StyledHTMLElement & { isStickyMobile: boolean }>(
  ({ theme, isStickyMobile }) => ({
    display: 'flex',
    padding: isStickyMobile ? '6px 8px' : '10px 8px',
    gap: '16px',
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius,
    [theme.mediaQueries.viewport7]: {
      padding: '8px',
      boxShadow: '0px 5px 32px rgba(0, 0, 0, 0.07)'
    }
  })
)

const FulfillmentToggleWrapper = styled.div<StyledHTMLElement>(({ theme }) => ({
  width: '170px',
  alignSelf: 'center',
  [theme.mediaQueries.viewport4]: {
    width: '190px'
  }
}))

const SummaryContent = styled.div(() => ({
  display: 'flex',
  gap: '10px',
  flex: 1
}))

export default Fulfillment
